// screens/Todos.tsx — B17b: the to-dos. Doing first, then open (deferred
// ones under a quiet "later" fold — defer_until honoured), done last and
// small (Kept's own pattern). Actions in place: start / complete / drop.
// Quick-add at the top — files-are-approval, a typed to-do is real
// immediately; the Knot stays the capture path for anything richer.
//
// A to-do remembers where it came from: the source-thread link rides the
// row (the from-convention back). Overdue = stripe + word, never colour
// alone. The class chip (B18e: bug/enhancement) renders whatever the row
// carries — the server ships nothing yet, so nothing renders yet;
// classification bolts on without rework here.
//
// B18e: the server now ships it, so the chip is live — and tapping it is the
// RECLASSIFY. One tap, no mode, no dialog: the ledger keeps what the work
// used to be called, so changing your mind is cheap and nothing is lost by
// being wrong first.
import { useEffect, useState } from 'preact/hooks';
import { TASKS_CHANGED_EVENT } from '../lib/atlasChannel';
import {
  ApiError,
  classifyTask,
  completeTask,
  createTask,
  dropTask,
  listTasks,
  startTask,
  type TaskApi,
} from '../lib/api';
import './Todos.css';

const TODO_STATES: Array<{ id: string; label: string }> = [
  { id: 'now', label: 'Now' },
  { id: 'done', label: 'Done' },
  { id: 'dropped', label: 'Dropped' },
];

export function todoStateOptions(): Array<{ id: string; label: string }> {
  return TODO_STATES;
}

export interface TodosProps {
  tenantId: string;
  state?: string;
  onOpenThread: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready' };

function overdue(t: TaskApi): boolean {
  return !!t.dueAt && new Date(t.dueAt).getTime() < Date.now() && (t.status === 'open' || t.status === 'doing');
}

const TODOS_REREAD_MS = 60_000;

function deferred(t: TaskApi): boolean {
  return !!t.deferUntil && new Date(t.deferUntil).getTime() > Date.now();
}

function dueLabel(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function Todos({ tenantId, state, onOpenThread }: TodosProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [tasks, setTasks] = useState<TaskApi[]>([]);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const filter = TODO_STATES.some((s) => s.id === state) ? state! : 'now';

  function refresh() {
    return listTasks(tenantId)
      .then((rows) => { setTasks(rows); setLoad({ phase: 'ready' }); })
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the to-dos could not be read',
        });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Read again when Atlas acts on a to-do, when the tab comes back into view, and every minute while it is in
  // view — Google Tasks changes arrive through Core's 5-minute sync, not through this screen.
  useEffect(() => {
    const again = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener(TASKS_CHANGED_EVENT, again);
    document.addEventListener('visibilitychange', again);
    const timer = window.setInterval(again, TODOS_REREAD_MS);
    return () => {
      window.removeEventListener(TASKS_CHANGED_EVENT, again);
      document.removeEventListener('visibilitychange', again);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function quickAdd() {
    const trimmed = title.trim();
    if (!trimmed) return;
    setNote(null);
    setBusy('add');
    try {
      await createTask(tenantId, { title: trimmed });
      setTitle('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not added');
    } finally {
      setBusy(null);
    }
  }

  /** B18e — the other word, in one tap. Appends: this task having been
   *  called something else, and when, is kept. */
  async function reclassify(t: TaskApi) {
    const next = t.classification === 'bug' ? 'enhancement' : 'bug';
    setNote(null);
    setBusy(t.id);
    try {
      await classifyTask(tenantId, t.id, {
        classification: next,
        reason: `Re-read as ${next === 'bug' ? 'a bug' : 'an enhancement'}.`,
      });
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the change was not recorded');
    } finally {
      setBusy(null);
    }
  }

  async function act(t: TaskApi, action: 'start' | 'complete' | 'drop') {
    setNote(null);
    setBusy(t.id);
    try {
      const fn = action === 'start' ? startTask : action === 'complete' ? completeTask : dropTask;
      await fn(tenantId, t.id);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the change was not recorded');
    } finally {
      setBusy(null);
    }
  }

  function Row({ t }: { t: TaskApi }) {
    const active = t.status === 'open' || t.status === 'doing';
    return (
      <div class={`todos__row${overdue(t) ? ' is-overdue' : ''}`}>
        <div class="todos__row-main">
          <span class="todos__row-title">
            {overdue(t) ? <span class="todos__overdue-word">overdue · </span> : null}
            {t.title}
          </span>
          <span class="todos__row-meta">
            {t.dueAt ? <span class="todos__row-due">{dueLabel(t.dueAt)}</span> : null}
            {t.classification ? (
              <button
                type="button"
                class="todos__row-class todos__row-class--tappable"
                disabled={busy === t.id}
                aria-label={t.classification === 'bug' ? 'Re-read as an enhancement' : 'Re-read as a bug'}
                onClick={() => void reclassify(t)}
              >
                {t.classification}
              </button>
            ) : null}
            {t.sourceThreadId ? (
              <button type="button" class="todos__row-source" onClick={() => onOpenThread(t.sourceThreadId!)}>
                from the thread
              </button>
            ) : null}
          </span>
        </div>
        {active ? (
          <div class="todos__row-actions">
            {t.status === 'open' ? (
              <button type="button" class="todos__act" disabled={busy === t.id} onClick={() => void act(t, 'start')}>
                Start
              </button>
            ) : null}
            <button type="button" class="todos__act todos__act--primary" disabled={busy === t.id} onClick={() => void act(t, 'complete')}>
              Done
            </button>
            <button type="button" class="todos__act" disabled={busy === t.id} onClick={() => void act(t, 'drop')}>
              Drop
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const doing = tasks.filter((t) => t.status === 'doing');
  const open = tasks.filter((t) => t.status === 'open' && !deferred(t));
  const later = tasks.filter((t) => t.status === 'open' && deferred(t));
  // The MOST RECENT finishes — the lab walk caught the unsorted slice
  // showing the eight OLDEST while the one just completed fell outside it.
  const done = tasks
    .filter((t) => t.status === 'done')
    .sort((a, b) => (b.completedAt ?? b.updatedAt).localeCompare(a.completedAt ?? a.updatedAt))
    .slice(0, 8);
  const droppedRows = tasks.filter((t) => t.status === 'dropped' || t.status === 'cancelled');

  return (
    <div class="todos">
      <div class="todos__eyebrow">To-dos</div>

      <div class="todos__add">
        <input
          class="todos__add-input"
          type="text"
          placeholder="Add a to-do…"
          value={title}
          disabled={busy === 'add'}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void quickAdd(); }}
        />
        <button type="button" class="todos__act todos__act--primary" disabled={busy === 'add' || !title.trim()} onClick={() => void quickAdd()}>
          Add
        </button>
      </div>
      {note ? <div class="todos__note">{note}</div> : null}

      {load.phase === 'loading' ? (
        <div class="todos__skeleton" aria-hidden="true">
          <div class="todos__skeleton-row" />
          <div class="todos__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="todos__say">
          The to-dos could not be read.
          <br />
          <span class="todos__say-soft">{(load as { message: string }).message}</span>
        </div>
      ) : filter === 'done' ? (
        tasks.filter((t) => t.status === 'done').length === 0 ? (
          <div class="todos__say">Nothing finished yet.</div>
        ) : (
          <div class="todos__list">
            {tasks.filter((t) => t.status === 'done').map((t) => <Row t={t} key={t.id} />)}
          </div>
        )
      ) : filter === 'dropped' ? (
        droppedRows.length === 0 ? (
          <div class="todos__say">Nothing dropped.</div>
        ) : (
          <div class="todos__list">{droppedRows.map((t) => <Row t={t} key={t.id} />)}</div>
        )
      ) : (
        <>
          {doing.length + open.length + later.length === 0 ? (
            // The empty working set — but a finish just made NEVER hides
            // behind it (the walk caught exactly that: complete the last
            // task and it looked swallowed). The Done fold below renders
            // either way.
            <div class="todos__say">
              Nothing on the list.
              <br />
              <span class="todos__say-soft">
                Type one above, or let a thread hand one over — a to-do remembers where it came from.
              </span>
            </div>
          ) : null}
          {doing.length > 0 ? (
            <section class="todos__group">
              <h2 class="todos__group-title">Doing</h2>
              {doing.map((t) => <Row t={t} key={t.id} />)}
            </section>
          ) : null}
          {open.length > 0 ? (
            <section class="todos__group">
              <h2 class="todos__group-title">Open</h2>
              {open.map((t) => <Row t={t} key={t.id} />)}
            </section>
          ) : null}
          {later.length > 0 ? (
            <section class="todos__group">
              <h2 class="todos__group-title">Later</h2>
              {later.map((t) => <Row t={t} key={t.id} />)}
            </section>
          ) : null}
          {done.length > 0 ? (
            <section class="todos__group todos__group--done">
              <h2 class="todos__group-title">Done</h2>
              {done.map((t) => (
                <div class="todos__done-line" key={t.id}>{t.title}</div>
              ))}
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
